<template>
  <div class="ClassificationRule">
    <Loader text="Loading Classification Rule" v-if="!Rule" />
    <div>
      <header class="ClassificationRule__header">
        <div
          class="ClassificationRule__header-container d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <p class="ClassificationRule__header-title">
              <router-link
                to="/classification-rules"
                tag="span"
                class="ClassificationRule__header-title-link"
              >
                Classification Rules
              </router-link>
              <v-icon small class="ClassificationRule__header-title-icon">
                mdi-chevron-right
              </v-icon>
              <span class="ClassificationRule__header-title-name" v-if="Rule.documentType">
                {{ Rule.documentType }}
              </span>
              <span class="ClassificationRule__header-title-no-name" v-else>
                Rule document type here
              </span>
            </p>
          </div>

          <div class="d-flex justify-space-between">
            <div class="ClassificationRule__header-button-divider"></div>
            <v-btn
              color="primary"
              elevation="0"
              :disabled="updateRuleLoading"
              @click="save"
              :loading="updateRuleLoading"
            >
              Save Rule
            </v-btn>
          </div>
        </div>
      </header>

      <div class="ClassificationRule__main-content">
        <v-row>
          <v-col cols="12" lg="8">
            <v-card class="RuleSettings" :elevation="1">
              <v-card-title>Classification Rule Settings</v-card-title>
              <v-card-text>
                <v-form ref="ruleForm" v-model="formValid">
                  <v-text-field
                    v-model="localRule.documentType"
                    label="Document Type"
                    placeholder="e.g., Invoice, Medical Report, Contract"
                    outlined
                    :rules="documentTypeRules"
                    class="mb-3"
                  />

                  <v-textarea
                    v-model="localRule.description"
                    label="Description"
                    placeholder="Describe the characteristics that identify this document type..."
                    outlined
                    rows="6"
                    :rules="descriptionRules"
                  />
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" lg="4">
            <v-card class="TestWidget TestWidget--sticky" :elevation="1">
              <v-card-title>Test Classification Rule</v-card-title>
              <v-card-text>
                <div class="mb-4">
                  <v-file-input
                    v-model="testFile"
                    label="Upload PDF File"
                    accept=".pdf"
                    outlined
                    prepend-icon="mdi-file-pdf-box"
                    :loading="fileProcessing"
                    @change="handleFileUpload"
                    show-size
                  />
                </div>

                <v-btn
                  color="primary"
                  block
                  elevation="0"
                  :disabled="!testFileBase64 || testLoading"
                  :loading="testLoading"
                  @click="runTest"
                >
                  TEST
                </v-btn>

                <div v-if="testResult" class="mt-4">
                  <v-alert
                    :type="testResult.success ? 'success' : 'error'"
                    dense
                    text
                  >
                    Test {{ testResult.success ? 'Completed' : 'Failed' }}
                  </v-alert>
                  <div v-if="testResult.message" class="mt-2">
                    <v-textarea
                      :value="testResult.message"
                      label="Test Result"
                      readonly
                      outlined
                      dense
                      rows="3"
                    />
                  </div>
                  <div v-if="testResult.classificationResult" class="mt-2">
                    <div class="d-flex align-center mb-2">
                      <v-icon left small>mdi-file-document-outline</v-icon>
                      <span class="text-subtitle2">Classification Result:</span>
                    </div>
                    <json-viewer
                      v-if="testResult.classificationResult !== undefined && testResult.classificationResult !== ''"
                      :value="testResult.classificationResult"
                      :expand-depth="0"
                      boxed
                      sort
                      copyable
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import JsonViewer from 'vue-json-viewer';
import Loader from '@/components/loader/loader.vue';

const { mapGetters: rulesGetters, mapActions: rulesActions } =
    createNamespacedHelpers('classificationRules');
const { mapActions: workflowDetailsActions } =
    createNamespacedHelpers('workflows/details');

export default {
  name: 'ClassificationRuleDetail',
  components: {
    Loader,
    JsonViewer,
  },
  data() {
    return {
      formValid: false,
      localRule: {
        documentType: '',
        description: '',
      },
      documentTypeRules: [
        v => !!v || 'Document type is required',
        v => (v && v.length <= 100) || 'Document type must be less than 100 characters',
      ],
      descriptionRules: [
        v => !!v || 'Description is required',
        v => (v && v.length <= 1000) || 'Description must be less than 1000 characters',
      ],
      // Test widget data
      testFile: null,
      testFileBase64: null,
      fileProcessing: false,
      testLoading: false,
      testResult: null,
      checkStatusInterval: undefined,
      checkStatusData: {
        action: {},
        task: {}
      },
    };
  },
  computed: {
    ...rulesGetters({
      Rule: 'RULE',
      updateRuleLoading: 'UPDATE_RULE_LOADING',
      fetchRuleLoading: 'FETCH_RULE_LOADING',
    }),
    ruleId() {
      return this.$route.params.ruleId;
    },
  },
  watch: {
    Rule: {
      handler(newRule) {
        if (newRule) {
          this.localRule = {
            documentType: newRule.documentType || '',
            description: newRule.description || '',
          };
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    if (this.ruleId) {
      this.fetchRule(this.ruleId);
    }
  },
  methods: {
    ...rulesActions(['fetchRule', 'updateRule']),
    ...workflowDetailsActions(['executeSingleAction', 'getWorkflowTask']),
    async save() {
      if (!this.$refs.ruleForm.validate()) {
        return;
      }

      try {
        await this.updateRule({
          ruleId: this.ruleId,
          ruleData: {
            documentType: this.localRule.documentType,
            description: this.localRule.description,
          },
        });
      } catch (error) {
        console.error('Error saving rule:', error);
      }
    },

    // File upload handling
    async handleFileUpload(file) {
      if (!file) {
        this.testFileBase64 = null;
        this.testResult = null;
        return;
      }

      this.fileProcessing = true;
      this.testResult = null;

      try {
        // Convert PDF to base64
        const base64 = await this.convertFileToBase64(file);
        this.testFileBase64 = base64;
      } catch (error) {
        console.error('Error processing file:', error);
        this.testResult = {
          success: false,
          message: 'Error processing PDF file. Please try again.',
        };
      } finally {
        this.fileProcessing = false;
      }
    },

    convertFileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Remove data:application/pdf;base64, prefix to get pure base64
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    // Test functionality
    async runTest() {
      if (!this.testFileBase64) {
        return;
      }

      this.testLoading = true;
      this.testResult = null;

      const options = {
        creds: null, // Will use system credentials for AI service
        data: {
          classifications: [{
            isCustom: true,
            description: this.localRule.description,
            documentType: this.localRule.documentType,
          }],
          classify: true,
          visualMode: true,
          file: "Object.file",
        },
        action: 'ai_document_analysis',
        input: {
          file: this.testFileBase64,
        },
        isServerless: true,
      };

      const action = {
        input: {
          file: this.testFileBase64,
        }
      };

      try {
        const data = await this.executeSingleAction(options);
        if (data.success) {
          action.output = data;
          this.testResult = {
            success: true,
            message: 'Classification completed successfully',
            classificationResult: data,
            taskId: data.taskId
          };
        } else {
          this.testResult = {
            success: false,
            message: data.message || 'Classification failed',
          };
        }
        this.testLoading = false;
      } catch (error) {
        console.error('Classification test failed:', error);
        this.testResult = {
          success: false,
          message: error.message || 'Classification test failed',
        };
        this.testLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ClassificationRule {
  &__header {
    background: white;
    border-bottom: 1px solid #e0e0e0;
    padding: 16px 24px;

    &-container {
      max-width: 1200px;
      margin: 0 auto;
    }

    &-title {
      font-size: 24px;
      font-weight: 500;
      margin: 0;
      display: flex;
      align-items: center;

      &-link {
        color: #666;
        cursor: pointer;
        text-decoration: none;

        &:hover {
          color: #1976d2;
        }
      }

      &-icon {
        margin: 0 8px;
        color: #666;
      }

      &-name {
        color: #333;
      }

      &-no-name {
        color: #999;
        font-style: italic;
      }
    }

    &-button-divider {
      width: 1px;
      height: 32px;
      background: #e0e0e0;
      margin-right: 16px;
    }
  }

  &__main-content {
    padding: 24px;
    min-height: calc(100vh - 120px);

    .v-row {
      justify-content: center;
    }
  }

  .RuleSettings {
    .v-card-title {
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
    }

    .v-card-text {
      padding: 24px;
    }
  }

  .TestWidget {
    .v-card-title {
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
    }

    .v-card-text {
      padding: 24px;
    }

    &--sticky {
      position: sticky;
      top: 85px;
      max-height: calc(100vh - 60px);
      overflow-y: auto;

      transition: box-shadow 0.2s ease-in-out;

      &:hover {
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12) !important;
      }

      .v-card-text {
        max-height: calc(100vh - 200px);
        overflow-y: auto;
      }
    }
  }
}
</style>